import { useCallback, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import {
  DESTINATIONS,
  DESTINATIONS_BY_ID,
  DEVELOPER_DESTINATIONS_BY_ID,
  SIMPLE_LABELS,
  STAGE_HOME,
  STAGE_LABEL,
  type NavigatorSpec,
  type Stage,
} from "@/app/destinations";
import {
  CAPTURE_BY_ID,
  CAPTURE_NAVIGATOR,
  SETTINGS_BY_ID,
  SETTINGS_NAVIGATOR,
  SIMPLE_SETTINGS_NAVIGATOR,
} from "@/app/surfaces";
import { TooltipProvider } from "@/components/ui/Tooltip";
import { useApp } from "@/lib/AppContext";
import { PhoneShell } from "@/mobile/PhoneShell";
import { useViewport } from "@/mobile/ViewportProvider";
import type {
  ChatConversation,
  Entity,
  LedgerView,
  Meeting,
  PersonCard,
  Recording,
  ReviewCounts,
  SourceMaterial,
  SynthesisBatchList,
  Verdict,
  WorldViewActivity,
  WorldViewIndex,
} from "@/lib/api";
import { describeWorldViewActivity } from "@/components/WorldViewActivityLine";
import { ENTITY_TYPES } from "@/lib/entityTypes";
import { formatRelative } from "@/lib/format";
import { useLedger } from "@/lib/LedgerContext";
import { NAVIGATOR_KIND_ORDER, subjectKindMeta } from "@/lib/subjectKinds";
import { useSynthesisBatches } from "@/lib/SynthesisBatchesContext";
import { useUiMode } from "@/lib/uiMode";
import { VERDICT_COLOUR } from "@/lib/verdicts";
import { nameOf, statusColour } from "@/pages/Recordings";
import { AppBar } from "./AppBar";
import { ContextualBar, type Breadcrumb } from "./ContextualBar";
import { DestinationRail } from "./DestinationRail";
import { NavigatorPanel, PANEL_DEFAULT_WIDTH } from "./NavigatorPanel";

interface ActiveSurface {
  navigator: NavigatorSpec;
  stage?: Stage;
  breadcrumbs: Breadcrumb[];
  /** Settings and every simple-mode page cap content at 760 px instead of 1120 px. */
  narrow: boolean;
  /**
   * Whether this address has a navigator panel at all.
   *
   * False on simple mode's four destinations and its capture page (UI-16), where the
   * handoff draws the rail directly against the content frame: the panel's job is filters,
   * counts and sub-lists, and those are the machinery this surface exists to hide. Settings
   * keeps its panel in both modes — the handoff *reduces its section list* rather than
   * taking it away, because a settings screen with no way to reach the other sections is
   * not simpler, only stranded.
   */
  panel?: boolean;
}

/** The label a meeting nests under its recording as, matching the recording detail page. */
function meetingLabel(meeting: Meeting): string {
  return meeting.title ?? "Untitled meeting";
}

/**
 * "All recordings, dot-coloured by status" — the same list the page shows, from the
 * same poll, so the panel and the page can never disagree about a row.
 *
 * Each recording nests its meetings underneath it (collapsed by default — the panel
 * only opens one when the search bar matches a meeting it holds rather than the
 * recording itself), from the same per-recording reads the recording detail page
 * makes, so the panel never invents a meeting the page would not also show.
 */
function recordingsNavigator(recordings: Recording[], meetingsByRecording: Record<string, Meeting[]>): NavigatorSpec {
  const spec = DESTINATIONS_BY_ID.get("recordings")!.navigator;

  return {
    ...spec,
    sections: spec.sections.map((section) =>
      section.id === "all"
        ? {
            ...section,
            rows: recordings.map((recording) => {
              const meetings = meetingsByRecording[recording.id] ?? [];

              return {
                id: recording.id,
                label: nameOf(recording),
                dot: statusColour(recording.status),
                to: `/recordings/${recording.id}`,
                rows: meetings.map((meeting) => ({
                  id: meeting.id,
                  label: meetingLabel(meeting),
                  // SPCH-13's "not a meeting" stretches never enter the content phase, so
                  // their dot reads as parked rather than borrowing one of the five states
                  // a real run can be in.
                  dot: meeting.notAMeeting ? "var(--text-muted)" : statusColour(meeting.status),
                  to: `/recordings/${recording.id}`,
                })),
              };
            }),
          }
        : section,
    ),
  };
}

/**
 * "By type, with counts" and "in this view" — the same entities the manager shows, from
 * the same list, narrowed by the type in the URL so the panel and the page agree.
 */
function entitiesNavigator(entities: Entity[], activeType: string | null): NavigatorSpec {
  const inView = entities.filter(
    (entity) => !activeType || activeType === "all" || entity.type === activeType,
  );

  return {
    title: "Entities",
    sections: [
      {
        id: "by-type",
        label: "By type",
        rows: [
          { id: "all", label: "All", count: entities.length, to: "/entities" },
          ...ENTITY_TYPES.map((meta) => ({
            id: meta.type,
            label: meta.plural,
            count: entities.filter((entity) => entity.type === meta.type).length,
            to: `/entities?type=${meta.type}`,
          })),
        ],
      },
      {
        id: "in-view",
        label: "In this view",
        rows: inView.map((entity) => ({
          id: entity.id,
          label: entity.name,
          count: entity.mentionCount,
          to: `/entities/${entity.id}`,
        })),
      },
    ],
  };
}

/**
 * CHAT-2's "Conversations": the member's threads, newest first, each opening itself in the
 * page beside the panel.
 *
 * A thread with no title is one nobody has asked anything in yet — the title is the first
 * question, so it arrives with that question rather than before it.
 */
function chatNavigator(conversations: ChatConversation[]): NavigatorSpec {
  const spec = DESTINATIONS_BY_ID.get("chat")!.navigator;

  return {
    ...spec,
    sections: spec.sections.map((section) =>
      section.id === "conversations"
        ? {
            ...section,
            rows: conversations.map((conversation) => ({
              id: conversation.id,
              label: conversation.title ?? "New conversation",
              to: `/chat?c=${conversation.id}`,
            })),
          }
        : section,
    ),
  };
}

/**
 * UI-24 — "In flight & waiting" and "Batches", from the same list the index page's table
 * draws, so the panel and the page cannot disagree about a batch either.
 *
 * The subtitle carries the backlog the same way the World view's own status line does
 * (`describeWorldViewActivity`) — a developer opening this destination is usually asking "is
 * anything happening right now", and that answer belongs above the fold rather than only in
 * a row.
 */
function processingNavigator(batches: SynthesisBatchList | null): NavigatorSpec {
  const spec = DEVELOPER_DESTINATIONS_BY_ID.get("processing")!.navigator;
  const rows = batches?.batches ?? [];
  const running = rows.filter((batch) => batch.runStatus === "queued" || batch.runStatus === "processing");

  const subtitle =
    batches === null
      ? undefined
      : running.length > 0
        ? `${running.length} batch${running.length === 1 ? "" : "es"} running`
        : batches.waitingCount > 0
          ? `${batches.waitingCount} insight${batches.waitingCount === 1 ? "" : "s"} waiting`
          : "Up to date";

  return {
    ...spec,
    subtitle,
    sections: [
      {
        id: "batches",
        label: "Batches",
        rows: rows.map((batch) => ({
          id: batch.id,
          label: formatRelative(batch.completedAt ?? batch.createdAt),
          count: batch.appliedCount,
          dot:
            batch.runStatus === "failed" || batch.batchStatus === "failed"
              ? "var(--destructive)"
              : batch.runStatus === "queued" || batch.runStatus === "processing"
                ? "var(--vl-bridge)"
                : batch.droppedCount > 0
                  ? "var(--warning)"
                  : "var(--success)",
          to: `/processing/${batch.id}`,
        })),
      },
    ],
  };
}

/**
 * "Everyone heard", counted by the meetings each was heard in — the same number the gallery
 * card shows, from the same list, so the panel and the page cannot disagree about a person.
 */
function peopleNavigator(people: PersonCard[]): NavigatorSpec {
  const spec = DESTINATIONS_BY_ID.get("people")!.navigator;

  return {
    ...spec,
    sections: spec.sections.map((section) =>
      section.id === "everyone"
        ? {
            ...section,
            rows: people.map((person) => ({
              id: person.id,
              label: person.name,
              count: person.meetingCount,
              to: `/people/${person.id}`,
            })),
          }
        : section,
    ),
  };
}

/**
 * "By subject type", "Needs you" and "Pages" — the same subjects the index draws, from the
 * same read, narrowed by the kind in the URL so the panel and the page agree.
 *
 * The kind rows are the handoff's six in the handoff's order, followed by any other kind
 * that actually has a subject behind it. That second half is not embellishment: the drawing
 * lists six of the nine, and a `topic` page reachable from nowhere would be a page the
 * navigator quietly hides.
 *
 * The Contradictions row is a count that navigates, not a place to resolve one. AUTO-1 puts
 * resolution in the review queue (M7.3) — a belief pair that can be resolved from two
 * screens is a belief pair that gets resolved twice.
 */
function worldViewNavigator(
  index: WorldViewIndex | null, activeKind: string | null, activity: WorldViewActivity | null,
): NavigatorSpec {
  const subjects = index?.subjects ?? [];
  const present = [...new Set(subjects.map((subject) => subject.kind))];
  const kinds = [...NAVIGATOR_KIND_ORDER, ...present.filter((kind) => !NAVIGATOR_KIND_ORDER.includes(kind))];

  const shown = subjects.filter(
    (subject) => !activeKind || activeKind === "all" || subject.kind === activeKind,
  );

  return {
    title: "World view",
    subtitle: describeWorldViewActivity(activity) ?? undefined,
    filter: "Find a subject…",
    sections: [
      {
        id: "subject-type",
        label: "By subject type",
        rows: [
          { id: "all", label: "All pages", count: subjects.length, to: "/world-view" },
          ...kinds.map((kind) => ({
            id: kind,
            label: subjectKindMeta(kind).plural,
            count: subjects.filter((subject) => subject.kind === kind).length,
            to: `/world-view?kind=${kind}`,
          })),
        ],
      },
      {
        id: "needs-you",
        label: "Needs you",
        rows: [
          {
            id: "contradictions",
            label: "Contradictions",
            count: index?.contradictionCount ?? 0,
            dot: "var(--destructive)",
          },
        ],
      },
      {
        id: "pages",
        label: "Pages",
        rows: shown.map((subject) => ({
          id: subject.slug,
          label: subject.title,
          count: subject.beliefCount,
          to: `/world-view/${subject.slug}`,
        })),
      },
    ],
  };
}

/**
 * "By type, with counts" — the same three numbers the rail badge is the sum of, from the same
 * read, so the badge, the panel and the page cannot disagree about how much is waiting.
 *
 * Corrections finally have a real count and a row that navigates (M13.1). They had neither
 * while SPCH-8's proposals lived on Transcript Review alone and nothing served them across
 * recordings; a row with an invented zero would have said something untrue about proposals
 * waiting somewhere else.
 */
function reviewNavigator(counts: ReviewCounts | null): NavigatorSpec {
  const spec = DESTINATIONS_BY_ID.get("review")!.navigator;

  return {
    ...spec,
    sections: spec.sections.map((section) =>
      section.id === "by-type"
        ? {
            ...section,
            rows: [
              { id: "all", label: "All", count: counts?.count ?? 0, to: "/review" },
              { id: "insights", label: "Insights", count: counts?.insights ?? 0, to: "/review?type=insights" },
              {
                id: "corrections",
                label: "Corrections",
                count: counts?.corrections ?? 0,
                to: "/review?type=corrections",
              },
              {
                id: "contradictions",
                label: "Contradictions",
                count: counts?.contradictions ?? 0,
                dot: (counts?.contradictions ?? 0) > 0 ? "var(--destructive)" : undefined,
                to: "/review?type=contradictions",
              },
              {
                id: "questions",
                label: "Questions",
                count: counts?.questions ?? 0,
                to: "/review?type=questions",
              },
            ],
          }
        : section,
    ),
  };
}

/**
 * UI-9's four filter sections, from the ledger's own read.
 *
 * Every count here comes out of the same object the page's rows do — that is what the
 * `LedgerProvider` is for, and it is why "mirror the same filters in the navigator panel and
 * the page so the two never disagree" is structural rather than remembered.
 *
 * Each row carries the *whole* current query with one facet changed, so filters compose
 * (7 days + discarded + from this source) instead of replacing each other, and the panel's
 * exact-match highlighting lands on the row that is actually selected.
 */
function ledgerNavigator(view: LedgerView | null, search: string): NavigatorSpec {
  const to = (key: string, value: string | null) => {
    const next = new URLSearchParams(search);
    if (value === null) next.delete(key);
    else next.set(key, value);
    // Arriving from a belief's provenance focuses one insight; changing a filter is a new
    // question, so the focus goes with it — the same rule the page's own controls follow.
    next.delete("insight");
    const rest = next.toString();
    return rest ? `/insights?${rest}` : "/insights";
  };

  const facet = (id: string) => view?.horizons.find((row) => row.id === id)?.count;

  // The default window is spelled by leaving the parameter out, so the row for it points at
  // the same address the page's own pill does. Otherwise the panel would fail to light the
  // window the page is plainly showing, which is the disagreement this whole shape avoids.
  const horizon = (id: string) => to("horizon", id === "7d" ? null : id);

  return {
    title: "Insights",
    sections: [
      {
        id: "horizon",
        label: "Time horizon",
        rows: [
          { id: "7d", label: "7 days", count: facet("7d"), to: horizon("7d") },
          { id: "30d", label: "30 days", count: facet("30d"), to: horizon("30d") },
          { id: "90d", label: "90 days", count: facet("90d"), to: horizon("90d") },
          { id: "all", label: "All time", count: facet("all"), to: horizon("all") },
        ],
      },
      {
        id: "status",
        label: "By status",
        rows: (view?.states ?? []).map((row) => ({
          id: row.id,
          label: LEDGER_STATES[row.id] ?? row.id,
          count: row.count,
          to: to("state", row.id === "all" ? null : row.id),
        })),
      },
      {
        id: "verdict",
        label: "By effect on beliefs",
        rows: (view?.verdicts ?? []).map((row) => ({
          id: row.id,
          label: row.id === "any" ? "Any effect" : row.id[0]!.toUpperCase() + row.id.slice(1),
          count: row.count,
          dot: row.id === "any" ? undefined : VERDICT_COLOUR[row.id as Verdict],
          to: to("verdict", row.id === "any" ? null : row.id),
        })),
      },
      {
        id: "source",
        label: "By source",
        rows: [
          {
            id: "all",
            label: "All sources",
            count: view?.states.find((row) => row.id === "all")?.count,
            to: to("source", null),
          },
          ...(view?.sources ?? []).map((row) => ({
            id: row.id,
            label: row.title ?? "Untitled source",
            count: row.count,
            to: to("source", row.id),
          })),
        ],
      },
    ],
  };
}

const LEDGER_STATES: Record<string, string> = {
  all: "All",
  approved: "Approved",
  pending: "Pending review",
  discarded: "Discarded",
};

/**
 * Maps the current route onto the shell's chrome.
 *
 * Deriving this from the path rather than having each page push its own breadcrumb
 * keeps the shell authoritative: a page cannot forget to describe itself, and a deep
 * link restores the full chrome on a cold load without the page having rendered yet.
 */
function useActiveSurface(
  mode: "full" | "simple",
  pathname: string,
  search: string,
  recent: SourceMaterial[],
  recordings: Recording[],
  meetingsByRecording: Record<string, Meeting[]>,
  entities: Entity[],
  people: PersonCard[],
  worldView: WorldViewIndex | null,
  worldViewActivity: WorldViewActivity | null,
  reviewCounts: ReviewCounts | null,
  ledger: LedgerView | null,
  conversations: ChatConversation[],
  synthesisBatches: SynthesisBatchList | null,
): ActiveSurface {
  return useMemo(() => {
    const surface = resolveSurface(
      mode, pathname, search, recent, recordings, meetingsByRecording, entities, people, worldView,
      worldViewActivity, reviewCounts, ledger, conversations, synthesisBatches);

    // The stage is the trail's own first link now — "Sources" before "Recordings" before
    // whatever the page itself is showing — rather than a fixed row of pills sitting
    // beside a breadcrumb that already said where the pipeline stood.
    if (!surface.stage) return surface;

    const stageCrumb = { label: STAGE_LABEL[surface.stage], to: STAGE_HOME[surface.stage] };
    const [first, ...rest] = surface.breadcrumbs;

    // Insights and World view are their own stage's home page, so prefixing them would
    // repeat the same name right after the stage that already said it — "Insights ›
    // Insights". One crumb, unlinked as the page itself already was, says it once.
    if (rest.length === 0 && first?.label === stageCrumb.label && !first.to) {
      return { ...surface, breadcrumbs: [{ label: stageCrumb.label }] };
    }

    return { ...surface, breadcrumbs: [stageCrumb, ...surface.breadcrumbs] };
  }, [mode, pathname, search, recent, recordings, meetingsByRecording, entities, people, worldView, worldViewActivity,
      reviewCounts, ledger, conversations, synthesisBatches]);
}

/**
 * The chrome for the current address, before the stage is folded into its breadcrumb —
 * kept as a separate, non-hook function so that folding is one change in one place
 * rather than a repeated tail on every branch's own return below.
 */
function resolveSurface(
  mode: "full" | "simple",
  pathname: string,
  search: string,
  recent: SourceMaterial[],
  recordings: Recording[],
  meetingsByRecording: Record<string, Meeting[]>,
  entities: Entity[],
  people: PersonCard[],
  worldView: WorldViewIndex | null,
  worldViewActivity: WorldViewActivity | null,
  reviewCounts: ReviewCounts | null,
  ledger: LedgerView | null,
  conversations: ChatConversation[],
  synthesisBatches: SynthesisBatchList | null,
): ActiveSurface {
    // UI-16 — the chrome simple mode wears, decided before every full-mode branch below
    // rather than after, so a route that has a full-mode room cannot accidentally bring
    // its navigator sections and its stage pills along into a surface that has neither.
    //
    // There is no navigator panel at all in simple mode (the shell does not render one), so
    // what is left to decide is the breadcrumb, the 760 px column and — for the four
    // destinations the rail leads to — what they are called here.
    if (mode === "simple") {
      const simple = simpleSurface(pathname, worldView);
      if (simple) return simple;
    }

    // The capture navigator with its "Recently added" section filled in. Built here
    // rather than in the descriptor because it is the one part of the panel that comes
    // from data, and the descriptor is deliberately free of anything that can change.
    const capturing: NavigatorSpec = {
      ...CAPTURE_NAVIGATOR,
      sections: CAPTURE_NAVIGATOR.sections.map((section) =>
        section.id === "recent"
          ? {
              ...section,
              rows: recent.map((source) => ({
                id: source.id,
                label: source.title ?? source.originalFilename ?? "Untitled source",
                to: `/sources/${source.id}`,
              })),
            }
          : section,
      ),
    };

    // Reached from the recordings list, from a "Recently added" row, or from a
    // duplicate prompt. A recording keeps the recordings panel and a document keeps the
    // capture one, so whichever room the source belongs to is the one still around it —
    // decided by whether the list has this id rather than by where the click came from,
    // because a deep link has no click behind it at all.
    if (pathname.startsWith("/sources/")) {
      const id = pathname.split("/")[2];
      const recording = recordings.find((candidate) => candidate.id === id);

      return recording
        ? {
            navigator: recordingsNavigator(recordings, meetingsByRecording),
            stage: "sources",
            breadcrumbs: [{ label: "Recordings", to: "/recordings" }, { label: nameOf(recording) }],
            narrow: false,
          }
        : {
            navigator: capturing,
            stage: "sources",
            breadcrumbs: [{ label: "Add a source", to: "/add/audio" }, { label: "Source" }],
            narrow: false,
          };
    }

    // UI-5's own room. Matched before the destination fallback below, which would
    // otherwise see "/recordings/…" as the recordings list and leave the breadcrumb
    // saying nothing about which recording is open.
    if (pathname.startsWith("/recordings/")) {
      const id = pathname.split("/")[2];
      const recording = recordings.find((candidate) => candidate.id === id);

      if (recording) {
        return {
          navigator: recordingsNavigator(recordings, meetingsByRecording),
          stage: "sources",
          breadcrumbs: [{ label: "Recordings", to: "/recordings" }, { label: nameOf(recording) }],
          narrow: false,
        };
      }

      // Not a top-level recording — a meeting split off one (SPCH-13) shares this same
      // route by its own id (`RecordingDetail.tsx` branches on `source.kind ===
      // "meeting"`), so it is one of the rows `meetingsByRecording` nests under a
      // recording rather than a row of `recordings` itself.
      let meeting: Meeting | undefined;
      let parentId: string | undefined;
      for (const [candidateParentId, meetings] of Object.entries(meetingsByRecording)) {
        const found = meetings.find((candidate) => candidate.id === id);
        if (found) {
          meeting = found;
          parentId = candidateParentId;
          break;
        }
      }
      if (meeting) {
        const parent = parentId ? recordings.find((candidate) => candidate.id === parentId) : undefined;

        return {
          navigator: recordingsNavigator(recordings, meetingsByRecording),
          stage: "sources",
          breadcrumbs: [
            { label: "Recordings", to: "/recordings" },
            { label: "Meetings", to: parent ? `/recordings/${parent.id}` : undefined },
            { label: meetingLabel(meeting) },
          ],
          narrow: false,
        };
      }

      // Neither list has this id yet — a cold deep link raced ahead of the polls that
      // would tell it which kind of row this is. "Recording" is the honest thing to say
      // for the moment before one of them answers, exactly as it was before either list
      // could be checked.
      return {
        navigator: recordingsNavigator(recordings, meetingsByRecording),
        stage: "sources",
        breadcrumbs: [{ label: "Recordings", to: "/recordings" }, { label: "Recording" }],
        narrow: false,
      };
    }

    // UI-11's room, and the manager behind it. Matched before the destination fallback,
    // which would see "/entities/…" as the list and leave the breadcrumb saying nothing
    // about which entity is open.
    if (pathname.startsWith("/entities")) {
      const detailId = pathname.startsWith("/entities/") ? pathname.split("/")[2] : undefined;
      const activeType = new URLSearchParams(search).get("type");
      const entity = detailId ? entities.find((candidate) => candidate.id === detailId) : undefined;

      return {
        navigator: entitiesNavigator(entities, activeType),
        stage: "insights",
        breadcrumbs: detailId
          ? [{ label: "Entities", to: "/entities" }, { label: entity ? entity.name : "Entity" }]
          : [{ label: "Entities" }],
        narrow: false,
      };
    }

    // UI-8's room, and the gallery behind it. Matched before the destination fallback for
    // the same reason the entity detail is: "/people/…" would otherwise read as the gallery
    // and leave the breadcrumb saying nothing about whose dossier is open.
    if (pathname.startsWith("/people")) {
      const detailId = pathname.startsWith("/people/") ? pathname.split("/")[2] : undefined;
      const person = detailId ? people.find((candidate) => candidate.id === detailId) : undefined;

      return {
        navigator: peopleNavigator(people),
        stage: "insights",
        breadcrumbs: detailId
          ? [{ label: "People", to: "/people" }, { label: person ? person.name : "Person" }]
          : [{ label: "People" }],
        narrow: false,
      };
    }

    // UI-10's room, and the index behind it. Matched before the destination fallback for
    // the same reason the others are: "/world-view/…" would otherwise read as the index and
    // leave the breadcrumb saying nothing about which subject is open.
    if (pathname.startsWith("/world-view")) {
      const slug = pathname.startsWith("/world-view/") ? pathname.split("/")[2] : undefined;
      const activeKind = new URLSearchParams(search).get("kind");
      const subject = slug ? worldView?.subjects.find((candidate) => candidate.slug === slug) : undefined;

      return {
        navigator: worldViewNavigator(worldView, activeKind, worldViewActivity),
        stage: "world",
        breadcrumbs: slug
          ? [{ label: "World view", to: "/world-view" }, { label: subject ? subject.title : "Subject" }]
          : [{ label: "World view" }],
        narrow: false,
      };
    }

    // INS-4 and AUTO-1's room. Its "By type" counts come from the same read the rail badge
    // does, and the type in the URL is what the page filters on.
    if (pathname.startsWith("/review")) {
      return {
        navigator: reviewNavigator(reviewCounts),
        stage: "insights",
        breadcrumbs: [{ label: "Review queue" }],
        narrow: false,
      };
    }

    // UI-9's room. The panel's four filter sections read the ledger's own answer, so the
    // counts beside a filter are the number of rows clicking it produces.
    if (pathname.startsWith("/insights")) {
      return {
        navigator: ledgerNavigator(ledger, search),
        stage: "insights",
        breadcrumbs: [{ label: "Insights" }],
        narrow: false,
      };
    }

    // CHAT-2's room. The panel lists the same threads the page opens, from the same read,
    // so deleting one cannot leave it in the panel.
    if (pathname.startsWith("/chat")) {
      return {
        navigator: chatNavigator(conversations),
        stage: "insights",
        breadcrumbs: [{ label: "Chat" }],
        narrow: false,
      };
    }

    // UI-24's room — not in `DESTINATIONS`, so it needs its own branch rather than the
    // fallback below, which would otherwise read "/processing" as no destination at all and
    // fall back to the World view's. A batch opened by id gets its own breadcrumb, the same
    // shape the World view's own subject branch uses.
    if (pathname.startsWith("/processing")) {
      const batchId = pathname.startsWith("/processing/") ? pathname.split("/")[2] : undefined;

      return {
        navigator: processingNavigator(synthesisBatches),
        stage: "world",
        breadcrumbs: batchId
          ? [{ label: "Insight processing", to: "/processing" }, { label: "Batch" }]
          : [{ label: "Insight processing" }],
        narrow: false,
      };
    }

    if (pathname.startsWith("/settings")) {
      const sectionId = pathname.split("/")[2];
      const section = sectionId ? SETTINGS_BY_ID.get(sectionId) : undefined;

      return {
        navigator: SETTINGS_NAVIGATOR,
        breadcrumbs: section
          ? [{ label: "Settings", to: "/settings" }, { label: section.label }]
          : [{ label: "Settings" }],
        narrow: true,
      };
    }

    if (pathname.startsWith("/add")) {
      const captureId = pathname.split("/")[2];
      const capture = captureId ? CAPTURE_BY_ID.get(captureId) : undefined;

      return {
        navigator: capturing,
        stage: "sources",
        breadcrumbs: capture ? [{ label: "Add a source" }, { label: capture.label }] : [{ label: "Add a source" }],
        narrow: false,
      };
    }

    const destination = DESTINATIONS.find((d) => pathname.startsWith(d.path)) ?? DESTINATIONS[0]!;

    return {
      navigator:
        destination.id === "recordings" ? recordingsNavigator(recordings, meetingsByRecording) : destination.navigator,
      stage: destination.stage,
      breadcrumbs: [{ label: destination.label }],
      narrow: false,
    };
}

/**
 * The chrome for one simple-mode address.
 *
 * Every simple screen is one 760 px column with its own heading, so the chrome above it is
 * only ever a breadcrumb — no stage pills, because the three pipeline stages are exactly
 * the machinery this surface exists to hide, and no panel toggle, because there is no panel
 * to toggle.
 *
 * Returns null for an address simple mode has no rendering of, which falls through to the
 * full-mode chrome below. That is deliberate rather than a gap: an address the simple rail
 * does not lead to still resolves — a link must not break for the mode it is opened in —
 * and it should look like what it actually is.
 */
function simpleSurface(pathname: string, worldView: WorldViewIndex | null): ActiveSurface | null {
  const crumbs = (breadcrumbs: Breadcrumb[]): ActiveSurface => ({
    navigator: { title: "", sections: [] },
    breadcrumbs,
    narrow: true,
    panel: false,
  });

  if (pathname.startsWith("/world-view/")) {
    const slug = pathname.split("/")[2];
    const subject = slug ? worldView?.subjects.find((candidate) => candidate.slug === slug) : undefined;

    return crumbs([
      { label: SIMPLE_LABELS["world-view"]!, to: "/world-view" },
      { label: subject ? subject.title : "Subject" },
    ]);
  }

  if (pathname.startsWith("/world-view")) return crumbs([{ label: SIMPLE_LABELS["world-view"]! }]);
  if (pathname.startsWith("/review")) return crumbs([{ label: SIMPLE_LABELS.review! }]);
  if (pathname.startsWith("/chat")) return crumbs([{ label: SIMPLE_LABELS.chat! }]);
  if (pathname.startsWith("/search")) return crumbs([{ label: SIMPLE_LABELS.search! }]);
  if (pathname.startsWith("/add")) return crumbs([{ label: "Add a source" }]);

  if (pathname.startsWith("/settings")) {
    const sectionId = pathname.split("/")[2];
    const section = sectionId ? SETTINGS_BY_ID.get(sectionId) : undefined;

    return {
      navigator: SIMPLE_SETTINGS_NAVIGATOR,
      breadcrumbs: section
        ? [{ label: "Settings", to: "/settings" }, { label: section.label }]
        : [{ label: "Settings" }],
      narrow: true,
    };
  }

  return null;
}

/**
 * UI-3 — four nested frames, outermost to innermost: app bar, destination rail,
 * navigator panel, content frame. Everything except the content frame sits on
 * `--sidebar`.
 *
 * One of two shells (M12). The phone app is not this layout narrowed — it has its own
 * Claude Design handoff and its own chrome (`PhoneShell`), and which one is on screen is
 * decided by the window with the app bar's toggle as an override. Both render the same
 * routes, so nothing below this line has to know which shell it is inside.
 */
export function AppShell({ initials }: { initials: string }) {
  const { viewport, framed: framedPreview } = useViewport();
  const { pathname, search } = useLocation();
  const {
    recentSources, recordings, meetingsByRecording, entities, people, worldView, worldViewActivity, setup,
    reviewCounts, conversations,
  } = useApp();
  const { view: ledger } = useLedger();
  const { batches: synthesisBatches } = useSynthesisBatches();
  const mode = useUiMode();

  const surface = useActiveSurface(
    mode, pathname, search, recentSources, recordings, meetingsByRecording, entities, people, worldView,
    worldViewActivity, reviewCounts, ledger, conversations, synthesisBatches);

  const [panelOpen, setPanelOpen] = useState(true);
  const [panelWidth, setPanelWidth] = useState(PANEL_DEFAULT_WIDTH);

  const togglePanel = useCallback(() => setPanelOpen((open) => !open), []);

  // An address with no panel is not a collapsed panel: the toggle goes too, because a
  // control whose only effect is to do nothing is worse than no control.
  const hasPanel = surface.panel !== false;
  const showPanel = hasPanel && panelOpen;

  // The rail, the panel and the contextual bar have no phone equivalents, so the phone
  // shell replaces the whole layout rather than hiding parts of it — the same reason the
  // handoff draws a separate shell instead of a responsive one.
  if (viewport === "phone") {
    return (
      <TooltipProvider delayDuration={300}>
        <div className="flex h-full flex-col overflow-hidden bg-[var(--sidebar)] text-[var(--text-primary)]">
          {/* The desktop preview keeps the app bar around the device frame, which is where
              the toggle back to the desktop shell lives. A real phone has no room for a
              second bar above a 52 px header, and its own chrome already says what app
              this is. */}
          {framedPreview && <AppBar initials={initials} />}
          <PhoneShell />
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      {/* `h-full` rather than `h-dvh`: html, body and #root are already a 100% height
          chain, and tying the shell to that measures the real box instead of asking
          the viewport unit to resolve. The rail's overflow budget is a measurement of
          this element's descendants, so a shell that is taller than the window would
          silently mean nothing ever collapses. */}
      <div className="flex h-full flex-col overflow-hidden bg-[var(--sidebar)] text-[var(--text-primary)]">
        <AppBar initials={initials} />

        <div className="flex min-h-0 flex-1">
          {/* UI-13 — from the same read the Setup page draws, so the dot and the page
              can never disagree about whether something needs attention. Off until the
              first answer arrives: a dot that flashes on every cold load is a dot
              nobody looks at. */}
          <DestinationRail
            panelOpen={showPanel}
            // Off in simple mode, and not because the checklist stopped mattering: Setup is
            // not one of the four sections that surface offers, so a dot pointing at it
            // would be the machinery asking for attention through the one surface built to
            // keep it out of sight.
            setupIncomplete={mode !== "simple" && (setup?.blocking ?? 0) > 0}
            // Two rails, two screens, two numbers. Full mode's badge counts what its Review
            // queue holds; simple mode's counts what its Needs you merges (UI-16).
            reviewCount={(mode === "simple" ? reviewCounts?.needsYou : reviewCounts?.count) ?? 0}
          />

          {showPanel && (
            <NavigatorPanel spec={surface.navigator} width={panelWidth} onWidthChange={setPanelWidth} />
          )}

          <ContentFrame
            narrow={surface.narrow}
            bar={
              <ContextualBar
                panelOpen={panelOpen}
                onTogglePanel={togglePanel}
                showPanelToggle={hasPanel}
                breadcrumbs={surface.breadcrumbs}
              />
            }
          >
            <Outlet />
          </ContentFrame>
        </div>
      </div>
    </TooltipProvider>
  );
}

/**
 * Frame 4. No margin — it runs to the window edges right and bottom, and only its
 * top-left corner is rounded. The two low-opacity radial auras are what stop the
 * canvas reading as flat grey.
 */
function ContentFrame({
  narrow,
  bar,
  children,
}: {
  narrow: boolean;
  /** Kept full-bleed above the capped content column. */
  bar: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <main
      className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border border-[var(--border-subtle)] shadow-[var(--shadow-sm)]"
      style={{
        zoom: 0.9,
        borderRadius: "var(--radius-2xl) 0 0 0",
        background: [
          "radial-gradient(60% 60% at 25% 75%, color-mix(in oklch, var(--vl-virtual) 8%, transparent), transparent 70%)",
          "radial-gradient(55% 55% at 80% 20%, color-mix(in oklch, var(--vl-life) 7%, transparent), transparent 70%)",
          "var(--surface-0)",
        ].join(", "),
      }}
    >
      {/* The contextual bar stays full-bleed; only the page content is capped. */}
      {bar}

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full" style={{ maxWidth: narrow ? 760 : 1120 }}>
          {children}
        </div>
      </div>
    </main>
  );
}
